import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import { currentSession, signOut, SessionUser } from '../../config/previewAccess';
import AppCard from './AppCard';
import { LandingShell, Container, GhostButton, Eyebrow, H2, Sub, T } from './ui';

const FiCheck = FiIcons.FiCheck as React.ComponentType<any>;

/**
 * Approved-apps dashboard. Verifies the preview token against the backend
 * (/api/preview-auth/me); no valid session ⇒ redirect to /signin. Shows only the
 * apps the team cleared — the "your preview is ready" surface.
 */
const MyApps: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionUser | null>(null);

  useEffect(() => {
    let alive = true;
    currentSession().then(s => { if (alive) { setSession(s); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <LandingShell><Container><Loading>Loading your preview…</Loading></Container></LandingShell>;
  }
  if (!session) return <Navigate to="/signin" replace />;

  const approved = DEPLOYED_APPS.filter(a => session.approvedApps.includes(a.id));

  return (
    <LandingShell>
      <Container>
        <Ready>
          <Badge><FiCheck /></Badge>
          <div>
            <Eyebrow>Preview ready</Eyebrow>
            <H2>Welcome{session.name ? `, ${session.name.split(' ')[0]}` : ''} — your preview is ready</H2>
            <Sub>
              The team approved {approved.length} app{approved.length === 1 ? '' : 's'} for <b>{session.email}</b>. Open any below.
            </Sub>
          </div>
          <SignOut type="button" onClick={() => { signOut(); window.location.assign('/products'); }}>
            Sign out
          </SignOut>
        </Ready>

        {approved.length === 0 ? (
          <Empty>No apps are enabled on your account yet.</Empty>
        ) : (
          <Grid>{approved.map(app => <AppCard key={app.id} app={app} />)}</Grid>
        )}
      </Container>
    </LandingShell>
  );
};

export default MyApps;

const Loading = styled.div`padding: 60px; text-align: center; color: ${T.text2}; font-weight: 600;`;
const Ready = styled.div`
  display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
  background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 20px;
  padding: 24px 26px; margin: 16px 0 22px; box-shadow: 0 6px 20px rgba(13,52,47,.06);
`;
const Badge = styled.div`
  width: 52px; height: 52px; flex-shrink: 0; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 24px; color: #fff;
  background: linear-gradient(135deg, ${T.ok}, ${T.accentDark});
`;
const SignOut = styled(GhostButton)`margin-left: auto; padding: 10px 16px; font-size: 13.5px;`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px;`;
const Empty = styled.div`
  padding: 40px; text-align: center; color: ${T.text2}; font-weight: 600;
  background: ${T.surface}; border: 1px dashed #cbd5d3; border-radius: 18px;
`;
